import { motion } from "motion/react";
import { PhoneCall, Pencil, ClipboardCheck, LifeBuoy } from "lucide-react";
export const roadmapSteps = [
  {
    icon: PhoneCall,
    title: "Set Up and Explore",
    description: `1- Create an  <a target="_blank" style='text-decoration: underline' href="https://youth.europa.eu/solidarity/register_en" rel="noopener noreferrer">ESC Portal </a> Account: Register and provide accurate personal information to build a complete profile. 2- Explore Opportunities: Familiarize yourself with the available projects and organizations, using filters to narrow options based on country, field, and dates. 3- Check Eligibility: Review project details and ensure the opportunity is suitable and accessible, including visa eligibility for Egyptians.`,
    tag: "Step 1",
    color: "from-amber-400 to-orange-500",
  },
  {
    icon: Pencil,
    title: "Apply and Interview",
    description:
      "1- Prepare Your Application: Craft a motivational letter, highlight relevant experience, and submit your application. 2- Attend the Interview: Be punctual, prepared, and ready to share why you're a great fit for the project. 3- Receive Acceptance: If selected, congratulations! Contact a sending organization to support you with the next steps.",
    tag: "Step 2",
    color: "from-pink-400 to-rose-500",
  },
  {
    icon: ClipboardCheck,
    title: "Connect with Your Sending Organization",
    description: `1- Reach Out for Support: Contact us (or your chosen organization) to act as your sending organization. 2- Have a Dream process: Send us through email to request support, <a target="_blank" style='text-decoration: underline' href="https://docs.google.com/forms/d/e/1FAIpQLSehNymnpAqvZbXCUJi-OzTIDKo2MZ1R2O0cWM4QnPZ94qjUbw/viewform" rel="noopener noreferrer">Complete this form</a>, schedule an interview and await confirmation of acceptance. 3- Plan Your Visa Process: With our guidance, you prepare the necessary documents for your visa application.`,
    tag: "Step 3",
    color: "from-purple-400 to-indigo-500",
  },
  {
    icon: LifeBuoy,
    title: "Prepare for Departure",
    description:
      "1- Pre-Departure Training: Attend a training session to learn more about the experience and cultural adaptation tips. 2- Pack Your Luggage: Get ready for your journey and ensure all essentials are packed. Safe travels!",
    tag: "Step 4",
    color: "from-blue-400 to-cyan-500",
  },
];

function parseDescription(description) {
  const items = description.split(/(?=\d+-)/);
  return items.map((item) => item.trim()).filter((item) => item.length > 0);
}

function splitBoldText(text) {
  let inTag = false;
  let inSingleQuote = false;
  let inDoubleQuote = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === "<" && !inSingleQuote && !inDoubleQuote) {
      inTag = true;
      continue;
    }

    if (char === ">" && !inSingleQuote && !inDoubleQuote) {
      inTag = false;
      continue;
    }

    if (char === "'" && inTag && !inDoubleQuote) {
      inSingleQuote = !inSingleQuote;
      continue;
    }

    if (char === '"' && inTag && !inSingleQuote) {
      inDoubleQuote = !inDoubleQuote;
      continue;
    }

    if (char === ":" && !inTag && !inSingleQuote && !inDoubleQuote) {
      return {
        bold: text.slice(0, i).trim(),
        rest: text.slice(i + 1).trim(),
      };
    }
  }

  return { bold: text.trim(), rest: "" };
}

export function RoadmapComponent() {
  return (
    <section className="py-20 px-4 overflow-hidden">
      <div className="max-w-10xl mx-auto">
        <div className="relative">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-purple-400 to-emerald-400 origin-left"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {roadmapSteps.map((step, index) => {
              const Icon = step.icon;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="relative"
                >
                  {/* Card */}
                  <motion.div
                    whileHover={{ y: -8 }}
                    className="relative bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-2xl transition-shadow"
                  >
                    {/* Gradient Icon */}
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.15 + 0.2 }}
                      className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 shadow-lg`}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </motion.div>

                    {/* Tag */}
                    <div className="inline-block px-3 text-sm py-1 bg-slate-100 rounded-full text-slate-700 mb-3">
                      {step.tag}
                    </div>

                    {/* Content */}
                    <h3 className="text-slate-900 mb-3">{step.title}</h3>

                    {/* Parsed list items */}
                    <div className="space-y-2">
                      {parseDescription(step.description).map(
                        (item, itemIndex) => {
                          const [number, ...rest] = item.split("-");
                          const text = rest.join("-").trim();
                          const { bold, rest: normal } = splitBoldText(text);

                          return (
                            <div key={itemIndex} className="flex gap-2 text-xs">
                              <span className="text-slate-500 flex-shrink-0">
                                {number.trim()}-
                              </span>
                              <p className="text-slate-600">
                                <span
                                  className="font-medium text-slate-900"
                                  dangerouslySetInnerHTML={{
                                    __html: normal ? `${bold}:` : bold,
                                  }}
                                />
                                {normal ? (
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: ` ${normal}`,
                                    }}
                                  />
                                ) : null}
                              </p>
                            </div>
                          );
                        }
                      )}
                    </div>

                    {/* Animated arrow connector - desktop only */}
                    {index < roadmapSteps.length - 1 && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.5,
                          delay: index * 0.15 + 0.4,
                        }}
                        className="hidden lg:block absolute top-20 -right-4 z-10"
                      >
                        <div
                          className={`w-8 h-8 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center`}
                        >
                          <svg
                            className="w-4 h-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
